// File: frontend/src/components/DemandDriversScreen.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import ValuationFrame from "./ValuationFrame";
import { trackFunnelError, trackFunnelStepComplete, trackFunnelStepView } from "../utils/analytics";

function DemandDriversScreen() {
  const navigate = useNavigate();

  useEffect(() => {
    trackFunnelStepView(3, "demand_drivers_form");
  }, []);

  useEffect(() => {
    localStorage.removeItem("brandOperated");
    localStorage.removeItem("hasEnergyCertificate");
  }, []);

  const [formData, setFormData] = useState({
    category: localStorage.getItem("category") || "",
    locationChoice: localStorage.getItem("locationChoice") || "",
    demandDriversChoice: localStorage.getItem("demandDriversChoice") || "",
    publicTransportChoice: localStorage.getItem("publicTransportChoice") || "",
    capexCondition: localStorage.getItem("capexCondition") || "",
    brandOperated: false,
    hasEnergyCertificate: false,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors({});
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = "Hotel category is required.";
    if (!formData.locationChoice) {
      newErrors.locationChoice = "Location choice is required.";
    }
    if (!formData.demandDriversChoice) {
      newErrors.demandDriversChoice = "Demand drivers choice is required.";
    }
    if (!formData.publicTransportChoice) {
      newErrors.publicTransportChoice =
        "Public transportation choice is required.";
    }
    if (!formData.capexCondition) {
      newErrors.capexCondition = "CAPEX condition is required.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      trackFunnelError(3, "demand_drivers_form", "validation_failed", {
        error_count: Object.keys(newErrors).length,
      });
      return;
    }

    localStorage.setItem("category", formData.category);
    localStorage.setItem("locationChoice", formData.locationChoice);
    localStorage.setItem("demandDriversChoice", formData.demandDriversChoice);
    localStorage.setItem(
      "publicTransportChoice",
      formData.publicTransportChoice
    );
    localStorage.setItem("capexCondition", formData.capexCondition);
    localStorage.setItem(
      "brandOperated",
      JSON.stringify(formData.brandOperated)
    );
    localStorage.setItem(
      "hasEnergyCertificate",
      JSON.stringify(formData.hasEnergyCertificate)
    );

    trackFunnelStepComplete(3, "demand_drivers_form");

    navigate("/valuation");
  };

  return (
    <ValuationFrame
      currentStep={2}
      title="Hotel Characteristics"
      subtitle="Capture the property qualities that help explain the operating assumptions and preliminary valuation range."
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <section className="fieldset">
          <h2 className="fieldset-title">Hotel Positioning</h2>
          <div className="form-stack">
            <label className="form-label field-inline">
              <div className="field-copy">
                <div className="label-text-container">
                  <span className="label-text">Hotel Category</span>
                  <span
                    data-tooltip-id="tooltip-category"
                    data-tooltip-content="Select the category that best describes your hotel."
                    className="tooltip-icon"
                  >
                    ?
                  </span>
                  <Tooltip id="tooltip-category" place="top" effect="solid" />
                </div>
              </div>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select Category</option>
                <option value="1 - Star">1 - Star</option>
                <option value="2 - Star">2 - Star</option>
                <option value="3 - Star">3 - Star</option>
                <option value="4 - Star">4 - Star</option>
                <option value="5 - Star">5 - Star</option>
              </select>
              {errors.category && (
                <p className="error-message">{errors.category}</p>
              )}
            </label>

            <label className="form-label field-inline">
              <div className="field-copy">
                <span className="label-text">Location Choice</span>
                <p className="helper">
                  Broad setting of the asset within its market.
                </p>
              </div>
              <select
                name="locationChoice"
                value={formData.locationChoice}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select Location</option>
                <option value="City center/CBD">City center/CBD</option>
                <option value="Semi center">Semi center</option>
                <option value="Perifery">Perifery</option>
                <option value="Suburbs">Suburbs</option>
                <option value="Highway/route hotel">Highway/route hotel</option>
                <option value="Close to airport">Close to airport</option>
                <option value="Leisure destination">Leisure destination</option>
              </select>
              {errors.locationChoice && (
                <p className="error-message">{errors.locationChoice}</p>
              )}
            </label>

            <label className="form-label field-inline">
              <div className="field-copy">
                <span className="label-text">Demand Drivers</span>
                <p className="helper">
                  Proximity to reasons guests choose the market.
                </p>
              </div>
              <select
                name="demandDriversChoice"
                value={formData.demandDriversChoice}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select Demand Driver Proximity</option>
                <option value="Very close">Very close</option>
                <option value="Close">Close</option>
                <option value="Not close">Not close</option>
              </select>
              {errors.demandDriversChoice && (
                <p className="error-message">{errors.demandDriversChoice}</p>
              )}
            </label>

            <label className="form-label field-inline">
              <div className="field-copy">
                <span className="label-text">Public Transportation</span>
                <p className="helper">
                  Accessibility can affect demand and review commentary.
                </p>
              </div>
              <select
                name="publicTransportChoice"
                value={formData.publicTransportChoice}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select Public Transport Proximity</option>
                <option value="Very close">Very close</option>
                <option value="Close">Close</option>
                <option value="Not close">Not close</option>
              </select>
              {errors.publicTransportChoice && (
                <p className="error-message">{errors.publicTransportChoice}</p>
              )}
            </label>
          </div>
        </section>

        <section className="fieldset">
          <h2 className="fieldset-title">Condition and Operating Standard</h2>
          <div className="form-stack">
            <label className="form-label field-inline">
              <div className="field-copy">
                <span className="label-text">CAPEX Condition</span>
                <p className="helper">
                  High-level condition signal for the preliminary review.
                </p>
              </div>
              <select
                name="capexCondition"
                value={formData.capexCondition}
                onChange={handleChange}
                className="select-field"
              >
                <option value="">Select CAPEX Condition</option>
                <option value="Fully renovated">Recently fully renovated</option>
                <option value="Good condition">Good condition</option>
                <option value="Poor condition">Poor condition</option>
              </select>
              {errors.capexCondition && (
                <p className="error-message">{errors.capexCondition}</p>
              )}
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                name="brandOperated"
                checked={formData.brandOperated}
                onChange={handleChange}
              />
              Built to international standards and/or operated by a brand
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                name="hasEnergyCertificate"
                checked={formData.hasEnergyCertificate}
                onChange={handleChange}
              />
              Property has energy certificates
            </label>
          </div>
        </section>

        <div className="button-row">
          <button
            type="button"
            className="button secondary-button"
            onClick={() => navigate("/region-selection")}
          >
            Back
          </button>
          <button type="button" onClick={handleSubmit} className="button">
            Continue
          </button>
        </div>
      </form>
    </ValuationFrame>
  );
}

export default DemandDriversScreen;
